<template>
  <div class="relative inline-block" data-attendance-status-dropdown>
    <button
      ref="triggerRef"
      @click.stop="toggle"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors"
      :class="badgeClass"
    >
      {{ label }}
      <ChevronDownIcon class="h-3 w-3 opacity-60" />
    </button>

    <!-- Teleported to <body> and positioned via fixed coords: the badge normally
         sits inside a table wrapped in overflow-x-auto, which (per the CSS overflow
         quirk where setting overflow-x forces overflow-y to auto too) clips an
         absolutely-positioned panel before it can render — invisible/unclickable
         options with no visible error. Fixed+teleported escapes that clipping. -->
    <Teleport to="body">
      <div
        v-if="open"
        data-attendance-status-dropdown
        class="fixed w-36 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg z-50 overflow-hidden"
        :style="{ top: panelPos.top + 'px', left: panelPos.left + 'px' }"
      >
        <button
          v-for="opt in options"
          :key="opt.value"
          @click.stop="select(opt.value)"
          class="w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors"
          :class="status === opt.value
            ? 'bg-surface-muted dark:bg-surface-muted-dark font-semibold text-fg-base dark:text-fg-base-dark'
            : 'text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark'"
        >
          <span class="h-2 w-2 rounded-full flex-shrink-0" :class="opt.dot" />
          {{ opt.label }}
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  status:         { type: String,  default: 'not reported' }, // current status: attended|excused|absent|not reported|scheduled
  label:          { type: String,  required: true },          // pre-formatted display text for the closed badge
  badgeClass:     { type: String,  required: true },           // pre-computed color classes for the closed badge
  // Backend guard (lessons.py _lesson_is_future): attended/absent are rejected more
  // than 24h before the lesson, so those options are hidden rather than offered and rejected.
  isTooFarFuture: { type: Boolean, default: false },
})
const emit = defineEmits(['select'])

const open = ref(false)
const triggerRef = ref(null)
const panelPos = ref({ top: 0, left: 0 })

const ALL_OPTIONS = [
  { value: 'not reported', label: 'Not Reported', dot: 'bg-fg-faint dark:bg-fg-faint-dark' },
  { value: 'attended',     label: 'Attended',      dot: 'bg-green-500' },
  { value: 'excused',      label: 'Excused',       dot: 'bg-yellow-500' },
  { value: 'absent',       label: 'Absent',        dot: 'bg-red-500' },
]

const options = computed(() =>
  props.isTooFarFuture
    ? ALL_OPTIONS.filter(o => o.value === 'not reported' || o.value === 'excused')
    : ALL_OPTIONS
)

const PANEL_WIDTH = 144 // w-36

const toggle = () => {
  if (!open.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect()
    // Flip to the left edge of the button if the panel would run off the right
    // side of the viewport (common for this column near the right of the table).
    const left = Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)
    panelPos.value = { top: rect.bottom + 4, left: Math.max(8, left) }
  }
  open.value = !open.value
}

const select = (value) => {
  open.value = false
  if (value !== props.status) emit('select', value)
}

const close = () => { open.value = false }

const onClickOutside = (e) => {
  if (!e.target.closest('[data-attendance-status-dropdown]')) open.value = false
}
onMounted(() => {
  document.addEventListener('click', onClickOutside, true)
  // Panel position is computed once on open; if the table (or page) scrolls while
  // it's open, just close it rather than tracking scroll to keep this simple.
  window.addEventListener('scroll', close, true)
  window.addEventListener('resize', close)
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true)
  window.removeEventListener('scroll', close, true)
  window.removeEventListener('resize', close)
})
</script>
